import { Subject } from 'rxjs';

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ElementRef,
  Component,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  Optional,
  Self,
  ViewChild,
  OnChanges,
  OnInit,
  DoCheck,
  SimpleChanges,
} from '@angular/core';
import { FormGroupDirective, NgControl, NgForm, ControlValueAccessor } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';

import { ISnInputFormFieldConfig } from '../../interfaces/input-form-field-config.interface';

class PasswordControlBase {
  constructor(
    public _parentForm: NgForm,
    public _parentFormGroup: FormGroupDirective,
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    public ngControl: NgControl,
  ) {}
}

@Component({
  selector: 'sn-password-control',
  templateUrl: './password.control.html',
  styleUrls: ['./password.control.scss'],
  inputs: ['disabled'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: SnPasswordControl,
    },
  ],
})
export class SnPasswordControl
  extends PasswordControlBase
  implements OnChanges, OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string>
{
  public static nextId = 0;

  public stateChanges: Subject<void> = new Subject();

  public disabled: boolean;

  public errorState: boolean;

  @Input()
  public field: ISnInputFormFieldConfig;

  @Input()
  get value(): string {
    return this._value;
  }
  set value(value: string) {
    if (value !== this.value) {
      this._value = value;
      this.stateChanges.next();
      this._controlValueAccessorChangeFn(value);
    }
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(req: boolean) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  @Input() public toggleMask: boolean;

  @HostBinding() public id = `sn-password-control-${SnPasswordControl.nextId++}`;
  @HostBinding('attr.aria-describedby') public describedBy = '';

  @ViewChild('input') public inputRef: ElementRef;

  public focused = false;
  public hidden = true;

  public controlType = 'sn-password-control';

  private _value: string;
  private _placeholder: string;
  private _required = false;

  constructor(
    @Optional() parentForm: NgForm,
    @Optional() parentFormGroup: FormGroupDirective,
    @Optional() @Self() public ngControl: NgControl,
    defaultErrorStateMatcher: ErrorStateMatcher,
  ) {
    super(parentForm, parentFormGroup, defaultErrorStateMatcher, ngControl);

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  @HostBinding('class.floating')
  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  get empty(): boolean {
    return !this.value;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled) {
      this.stateChanges.next();
    }
  }

  public ngOnInit(): void {
    this.stateChanges.next();
  }

  public ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  @HostListener('focus')
  public focusHandler(): void {
    this.onContainerClick(null);
  }

  public toggleVisibility(): void {
    this.hidden = !this.hidden;
  }

  public onFocusChanged(value: boolean): void {
    this.focused = value;

    if (!value) {
      this._controlValueAccessorTouchedFn();
    }
  }

  public setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  public onContainerClick(event: MouseEvent): void {
    this.inputRef.nativeElement.focus();
  }

  public writeValue(value: any): void {
    this.value = value;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public registerOnChange(fn: (value: any) => void): void {
    this._controlValueAccessorChangeFn = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._controlValueAccessorTouchedFn = fn;
  }

  private _controlValueAccessorChangeFn: (value: any) => void = () => {};
  private _controlValueAccessorTouchedFn: () => void = () => {};
}
